'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

export default function LegalLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const pathname = usePathname();

    const links = [
        { label: 'Privacy Policy', href: '/legal/privacy-policy', icon: 'ShieldCheckIcon' },
        { label: 'Terms of Service', href: '/legal/terms-of-service', icon: 'DocumentTextIcon' },
        { label: 'Security', href: '/legal/security', icon: 'LockClosedIcon' },
        { label: 'Cookie Policy', href: '/legal/cookie-policy', icon: 'CpuChipIcon' }, // Using CpuChip as placeholder for Cookie if generic CookieIcon isn't available, or stick to simple icons
    ];

    return (
        <div className="min-h-screen bg-background pt-24 pb-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-success mb-4">
                        Legal Center
                    </h1>
                    <p className="text-text-secondary max-w-2xl mx-auto text-lg">
                        Transparency and trust are at the core of AstikLearn. Here you'll find important information about how we protect your data and the terms of using our platform.
                    </p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                    {/* Sidebar Navigation */}
                    <aside className="lg:w-64 flex-shrink-0">
                        <div className="sticky top-24 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 overflow-hidden">
                            <nav className="flex flex-col gap-1">
                                {links.map((link) => {
                                    const isActive = pathname === link.href;
                                    return (
                                        <Link
                                            key={link.href}
                                            href={link.href}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${isActive
                                                    ? 'bg-primary/10 text-primary border border-primary/20'
                                                    : 'text-text-secondary hover:bg-white/5 hover:text-white border border-transparent hover:border-white/5'
                                                }`}
                                        >
                                            <Icon
                                                name={link.icon as any}
                                                size={20}
                                                className={isActive ? 'text-primary' : 'text-text-tertiary group-hover:text-white'}
                                            />
                                            <span className="font-medium relative z-10">{link.label}</span>
                                            {isActive && (
                                                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-50" />
                                            )}
                                        </Link>
                                    );
                                })}
                            </nav>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="flex-1 min-w-0">
                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-10 w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
                            {children}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
